import { FC, useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { CreateFormTheme } from '../../utils/themes';
import ImportIcon from '../../assets/icons/ImportIcon';
import PlaceholderImage from '../../assets/images/placeholder-image.jpg';
import { useGetPresignedUrlMutation } from '../../api/formsApiSlice';
import { useTheme } from '../../middleware/ThemeContext';
import { useToast } from '../toast/ToastContext';

const UploadSection = styled.div`
  display: flex;
  gap: ${CreateFormTheme.spacing.md};
  align-items: center;
`;

const ImagePreview = styled.img`
  width: 110px;
  height: 110px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
  background: ${CreateFormTheme.colors.headerBG};
  object-fit: contain;
`;

const UploadColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${CreateFormTheme.spacing.sm};
  min-width: 0;
`;

const UploadButton = styled.label`
  background: ${CreateFormTheme.colors.headerBG};
  color: ${CreateFormTheme.colors.textSecondary};
  padding: ${CreateFormTheme.spacing.sm} ${CreateFormTheme.spacing.md};
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${CreateFormTheme.spacing.sm};
  transition: opacity 0.2s;
  width: fit-content;
  font-family: 'Raleway', sans-serif;
  font-weight: 500;
  font-size: 14px;

  &:hover {
    opacity: 0.9;
  }
`;

const FileInfo = styled.span`
  color: ${({ theme }) => theme.CreateFormTheme.colors.textSecondary};
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 325px;
`;
interface ProfilePictureUploadProps {
  forPreview?: boolean;
  viewImageThumbnail?: boolean;
  folder?: string;
  isFileUpload?: boolean;
  onUploadSuccess: (url: string) => void;
  onUploadError?: (error: string) => void;
  /**
   * Optional initial preview URL for existing images/files (e.g. edit mode).
   * When provided, it is used as the initial thumbnail until the user uploads a new file.
   */
  initialPreviewUrl?: string | null;
}

const ALLOWED_EXTENSIONS = [
  // Images
  "jpg", "jpeg", "png", "gif", "bmp", "webp",

  // PDF
  "pdf",

  // Word
  "doc", "docx",

  // Excel
  "xls", "xlsx", "csv",

  // PowerPoint
  "ppt", "pptx",

  // Text
  "txt",

  // Zip
  "zip", "rar", "7z",
];

// Allowed MIME types
const ALLOWED_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/bmp",
  "image/webp",

  "application/pdf",

  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",

  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",

  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",

  "text/plain",

  "application/zip",
  "application/x-zip-compressed",
];

function isAllowedFile(file: File): boolean {
  const extension = file.name.split(".").pop()?.toLowerCase() || "";

  const isValidExtension = ALLOWED_EXTENSIONS.includes(extension);

  const isValidMimeType = ALLOWED_MIME_TYPES.includes(file.type);

  return isValidExtension && isValidMimeType;
}

const FileUpload: FC<ProfilePictureUploadProps> = ({
  forPreview = false,
  viewImageThumbnail,
  folder,
  isFileUpload = false,
  onUploadSuccess,
  onUploadError,
  initialPreviewUrl = null,
}) => {
  const { theme } = useTheme();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(initialPreviewUrl);
  const [getPresignedUrl] = useGetPresignedUrlMutation();
  // generate a unique id per instance to avoid collisions when multiple
  // FileUpload components are rendered on the same page
  const inputIdRef = useRef(`file-upload-${Math.random().toString(36).slice(2)}`);

  // cleanup any created object URLs to avoid memory leaks and ensure previews
  // do not bleed between component instances
  useEffect(() => {
    const current = previewUrl;
    return () => {
      if (current) URL.revokeObjectURL(current);
    };
  }, [previewUrl]);

  // When an initial preview URL is provided (e.g. in edit mode),
  // keep it in sync as long as the user has not selected a new file.
  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(initialPreviewUrl || null);
    }
  }, [initialPreviewUrl, selectedFile]);

  const showToast = useToast();
  // const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (!e.target.files?.[0]) return;
  //   const file = e.target.files[0];
  //   if (!file.type.startsWith('image/')) {
  //     onUploadError?.('Please upload an image file');
  //     return;
  //   }
  //   if (file.size > 5 * 1024 * 1024) {
  //     onUploadError?.('File size must be less than 5MB');
  //     return;
  //   }
  //   setSelectedFile(file);
  //   setPreviewUrl(URL.createObjectURL(file));
  //   await uploadFile(file);
  // };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;

    const file = e.target.files[0];

    // Image-only mode
    if (!isFileUpload && !file.type.startsWith("image/")) {
      onUploadError?.("Please upload an image file");
      return;
    }

    // All-file mode but only safe files
    if (isFileUpload && !isAllowedFile(file)) {
      onUploadError?.(
        "Invalid file type. Allowed: PDF, Word, Excel, PPT, Images, TXT, ZIP"
      );
      return;
    }

    // Size validation
    if (file.size > 5 * 1024 * 1024) {
      onUploadError?.("File size must be less than 5MB");
      return;
    }

    setSelectedFile(file);

    if (file.type.startsWith("image/")) {
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setPreviewUrl(null);
    }

    await uploadFile(file);
  };

  const uploadFile = async (file: File) => {
    try {
      setIsUploading(true);
      const { data } = await getPresignedUrl({
        fileName: file.name,
        fileType: file.type,
        folder: folder
      });
      if (!data?.url) {
        showToast(
          "Error",
          "Could not process the upload. Please make sure the file is valid and the internet connection is stable",
          "error"
        );
        return { key: "" };
      }
      await fetch(data?.url, {
        method: data?.method,
        headers: { Accept: "application/json", "Content-Type": file.type },
        body: file,
      });
      onUploadSuccess(data?.key);
    } catch (error) {
      onUploadError?.('Failed to upload. Please try again.');
      setSelectedFile(null);
      setPreviewUrl(null);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <UploadSection>
      {viewImageThumbnail && (
        <ImagePreview
          src={previewUrl || PlaceholderImage}
          alt="Preview"
        />
      )}
      <UploadColumn>
        <input
          type="file"
          id={inputIdRef.current}
          accept={
            isFileUpload
              ? ".jpg,.jpeg,.png,.gif,.bmp,.webp,.pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.zip,.rar,.7z"
              : ".jpg,.jpeg,.png,.gif,.bmp,.webp"
          }
          onChange={handleFileChange}
          disabled={isUploading}
          hidden
        />
        <UploadButton
          htmlFor={inputIdRef.current}
          style={{
            pointerEvents: (forPreview || isUploading) ? 'none' : 'auto',
            opacity: (forPreview || isUploading) ? 0.6 : 1
          }}
        >
          <ImportIcon />
          {isUploading ? 'Uploading...' : 'Upload File'}
        </UploadButton>
        <FileInfo theme={theme}>
          {selectedFile
          ? selectedFile.name
          : isFileUpload
            ? 'All file types allowed. Max size of 5MB'
            : 'JPG, JPE, JPEG, GIF, PNG or BMP. Max size of 5MB'}
        </FileInfo>
      </UploadColumn>
    </UploadSection>
  );
};

export default FileUpload;
